import React, { useState } from "react";

import { useTranslation } from "react-i18next";
import {
	TestOptionFields,
	FrontendProduct,
	FrontendOrderItem,
} from "node-library/dist/portal";

import { CollapsibleCard } from "../../../common-components/card";
import {
	CustomIconButton,
	KebabDropdownButton,
	TextButton,
} from "../../../common-components/button";
import {
	DeleteIcon,
	EditIcon,
	TickIcon,
	SmallEditIcon,
	SmallDeleteIcon,
} from "../../../common-components/svgIcons";
import { NeutralColor } from "node-library/dist/colors";
import { Divider, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import {
	calculateItemPrice,
	calculatePrice,
	formatPrice,
	getInstallFamiliarizeTranslationText,
	getIQSelectionAbbreviationText,
	getItemTagTextColor,
	getRepairOptionTranslationText,
	getServiceAmount,
} from "../../../helpers/sampleHelper";
import {
	InstallFamiliarizeOptions,
	OrderItemType,
	RepairOptions,
} from "node-library/dist/constants";

interface Props {
	item: FrontendOrderItem;
	currency: string;
	index: number;
	deleteItem: () => void;
	goToUpdateItem: () => void;
	isReadOnlyTable?: boolean;
	isNestedCard?: boolean;
}

export default function ItemCard(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const [expanded, setExpanded] = useState(true);

	const { item, currency, deleteItem, goToUpdateItem } = props;

	const [tagText, textColor, tagColor] = getItemTagTextColor(item, translation);

	const serviceAmount = getServiceAmount(item);

	const itemCardContent: React.ReactNode = (
		<div className="item-preview-list-content">
			<div
				style={{
					backgroundColor: tagColor,
					color: textColor,
				}}
				className="typography-caption item-tag"
			>
				{tagText}
			</div>
			<div className={`item-preview-list-row`}>
				<div className="item-preview-info-column sample-preview-basic-info">
					<div className="typography-body-01 wrap-text">
						{item.product.productName}
					</div>
				</div>
				<div className="item-preview-info-column sample-preview-basic-info">
					<div className="typography-heading-04 typography-bold">
						{formatPrice(calculateItemPrice(item), currency)}
					</div>
				</div>

				<div className="item-preview-button-column sample-preview-expand-button">
					<IconButton
						onClick={() => {
							setExpanded(!expanded);
						}}
					>
						{expanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
					</IconButton>
				</div>
			</div>
			{serviceAmount > 0 && (
				<div className="item-preview-info-column sample-preview-basic-info">
					<div className="typography-body-01 overflow-ellipsis-text grey-text">
						{`Service (${serviceAmount})`}
					</div>
				</div>
			)}
		</div>
	);

	const actionRow: React.ReactNode = (
		<div
			style={{
				display: props.isReadOnlyTable ? "none" : "flex",
				width: "100%",
				justifyContent: "end",
				margin: "-10px 0",
			}}
		>
			<TextButton
				id={"edit-item-button"}
				onClick={() => {
					props.goToUpdateItem();
				}}
				size="xsmall"
				buttonClassName="edit-item-button"
			>
				<SmallEditIcon></SmallEditIcon>
				<span className="typography-body-01">{translation("Edit")}</span>
			</TextButton>
			<TextButton
				id={"delete-item-button"}
				onClick={() => {
					props.deleteItem();
				}}
				size="xsmall"
				buttonClassName="edit-item-button"
			>
				<SmallDeleteIcon></SmallDeleteIcon>
				<span className="typography-body-01">{translation("Delete")}</span>
			</TextButton>
		</div>
	);
	const collapsibleContent: React.ReactNode = (
		<>
			<div
				className={`item-list-row item-preview-collapsible-content ${
					props.isReadOnlyTable ? "read-only" : ""
				}`}
			>
				<div className="item-preview-info-column item-preview-expanded-instrument-info-column">
					<div className="typography-body-01 typography-bold view-item-info-image-row">
						<div>{translation("InstrumentInformation")}</div>
					</div>
					{item.itemType === OrderItemType.INSTRUMENT &&
						Boolean(item.product.warranty.enabled) && (
							<div className="typography-body-01 view-item-info-text-row">
								{`${translation("Warranty")}: `}
								<span>
									{`${item.product.warranty.period} ${item.product.warranty.unit}`}
								</span>
							</div>
						)}
					{item.itemType === OrderItemType.SERVICE && (
						<>
							<div className="typography-body-01 view-item-info-text-row">
								{`${translation("Source")}: `}
								<span>{`${item.product.source}`}</span>
							</div>
							{item.product.partSerialPairList && (
								<div
									className="typography-body-01 view-item-info-text-row"
									style={{ display: "grid" }}
								>
									{`${translation("PartNumberSerial")}: `}
									{item.product.partSerialPairList.map((pair, index) => (
										<div>{`${index + 1}. ${pair.partNumber} (${
											pair.serialNumber
										})`}</div>
									))}
								</div>
							)}
						</>
					)}
					<div className="typography-body-02 view-item-info-description-row view-item-info-info-text">
						{`${translation("Description")}: `}
						<span className="wrap-text" style={{ whiteSpace: "pre-line" }}>
							{item.product.productDescription}
						</span>
					</div>

					{item.itemType === OrderItemType.INSTRUMENT && (
						<>
							<Divider
								orientation="horizontal"
								sx={{ borderStyle: "dashed" }}
								flexItem
							></Divider>
							<div className="typography-body-02 view-item-info-text-row view-item-info-price-row">
								<span>
									{`${translation("UnitPrice")}: ${formatPrice(
										item.product.unitPrice,
										currency
									)}`}
								</span>
								<span>
									{`${translation("Quantity")}: ${item.product.quantity}`}
								</span>
								<span className="typography-bold">
									{`${translation("Total")}: ${formatPrice(
										calculatePrice(
											item.product.unitPrice,
											item.product.quantity
										),
										currency
									)}`}
								</span>
							</div>
						</>
					)}
				</div>
				{item.service && (
					<div className="item-preview-info-column item-preview-expanded-instrument-info-column">
						<div className="typography-body-01 typography-bold view-item-info-image-row">
							<div>{translation("ServiceInformation")}</div>
						</div>
						{Boolean(item.service.installFamiliarize.enabled) && (
							<div className="typography-body-01 view-item-info-text-row">
								<TickIcon></TickIcon>
								<span>
									{`${translation(
										"InstallationAndFarmilirization"
									)} (${getInstallFamiliarizeTranslationText(
										item.service.installFamiliarize
											.option as InstallFamiliarizeOptions,
										translation
									)})`}
								</span>
							</div>
						)}
						{Boolean(item.service.instrumentQualification.enabled) && (
							<div className="typography-body-01 view-item-info-text-row">
								<TickIcon></TickIcon>
								<span>
									{`${translation(
										"Qualification"
									)} (${getIQSelectionAbbreviationText(
										item.service.instrumentQualification,
										translation
									)})`}
								</span>
							</div>
						)}
						{Boolean(item.service.repair.enabled) && (
							<div className="typography-body-01 view-item-info-text-row">
								<TickIcon></TickIcon>
								<span>
									{`${translation("Repair")} (${getRepairOptionTranslationText(
										item.service.repair.option as RepairOptions,
										translation
									)})`}
								</span>
							</div>
						)}
						{Boolean(item.service.serviceContracts.enabled) && (
							<div className="typography-body-01 view-item-info-text-row">
								<TickIcon></TickIcon>
								<span>
									{`${translation("ServiceContracts")} (${
										item.service.serviceContracts.time
									} ${translation("Month")})`}
								</span>
							</div>
						)}
						<div className="typography-body-02 view-item-info-description-row view-item-info-info-text">
							{`${translation("Description")}: `}
							<span className="wrap-text" style={{ whiteSpace: "pre-line" }}>
								{item.service.description}
							</span>
						</div>
						<Divider
							orientation="horizontal"
							sx={{ borderStyle: "dashed" }}
							flexItem
						></Divider>
						<div className="typography-body-02 view-item-info-text-row view-item-info-price-row">
							<span>
								{`${translation("UnitPrice")}: ${formatPrice(
									item.service.unitPrice,
									currency
								)}`}
							</span>
							<span>
								{`${translation("Quantity")}: ${item.service.quantity}`}
							</span>
							<span className="typography-bold">
								{`${translation("Total")}: ${formatPrice(
									calculatePrice(item.service.unitPrice, item.service.quantity),
									currency
								)}`}
							</span>
						</div>
					</div>
				)}
				{actionRow}
			</div>
		</>
	);

	const itemCard: React.ReactNode = (
		<CollapsibleCard
			className={props.isNestedCard ? "" : "common-card"}
			expanded={expanded}
			content={itemCardContent}
			collapsibleContent={collapsibleContent}
		></CollapsibleCard>
	);

	return itemCard;
}
